import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { debtorApi } from "@/lib/api";

const PERFIL_OPTIONS = [
  { value: "Excelente", label: "💎  Excelente — Paga antes de fecha" },
  { value: "Cumplidor", label: "✅  Cumplidor — Paga a tiempo" },
  { value: "Intermitente", label: "⚠️  Intermitente — Requiere recordatorios" },
  { value: "Crítico", label: "🚨  Crítico — Mora recurrente" },
];

/**
 * Editar datos de contacto y notas del deudor. NO permite mover montos ni
 * cronograma (esos requieren registrar abonos o eliminar y volver a crear).
 */
export default function EditDebtorDialog({ open, onOpenChange, debtor, onSaved }) {
  const [form, setForm] = useState({
    cliente_nombre: "",
    email: "",
    telefono: "",
    perfil_cumplimiento: "",
    notas_comportamiento: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (debtor) {
      setForm({
        cliente_nombre: debtor.cliente_nombre || "",
        email: debtor.email || "",
        telefono: debtor.telefono || "",
        perfil_cumplimiento: debtor.perfil_cumplimiento || "",
        notas_comportamiento: debtor.notas_comportamiento || "",
      });
    }
  }, [debtor]);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.cliente_nombre.trim()) return toast.error("El nombre no puede estar vacío");
    if (form.telefono.trim() && form.telefono.trim().length < 7)
      return toast.error("El teléfono debe tener al menos 7 dígitos");
    setSaving(true);
    try {
      const patch = {
        cliente_nombre: form.cliente_nombre.trim(),
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        perfil_cumplimiento: form.perfil_cumplimiento || null,
        notas_comportamiento: form.notas_comportamiento.trim(),
      };
      await debtorApi.update(debtor.id, patch);
      toast.success("Datos actualizados");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  if (!debtor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-debtor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editar datos del cliente</DialogTitle>
          <DialogDescription>
            Solo se pueden editar datos de contacto y notas. Para cambiar montos o cronograma,
            registra un abono o elimina y vuelve a crear el registro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Cliente / Razón social <span className="text-destructive">*</span></Label>
            <Input
              data-testid="edit-cliente"
              value={form.cliente_nombre}
              onChange={(e) => update("cliente_nombre", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input
                data-testid="edit-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                data-testid="edit-telefono"
                value={form.telefono}
                onChange={(e) => update("telefono", e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Perfil de cumplimiento</Label>
            <Select
              value={form.perfil_cumplimiento}
              onValueChange={(v) => update("perfil_cumplimiento", v)}
            >
              <SelectTrigger data-testid="edit-perfil">
                <SelectValue placeholder="Seleccione un perfil…" />
              </SelectTrigger>
              <SelectContent>
                {PERFIL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notas de comportamiento</Label>
            <Textarea
              data-testid="edit-notas"
              rows={3}
              value={form.notas_comportamiento}
              onChange={(e) => update("notas_comportamiento", e.target.value)}
              placeholder="Ej: prefiere transferencia, paga los viernes, contactar al gerente…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="edit-save-btn">
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
